const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || 'https://medante.sk'

/**
 * Extracts the domain from the base URL (e.g., "medante.sk" from "https://medante.sk")
 */
const getDomainFromBaseUrl = (): string => {
	return BASE_URL.replace(/^https?:\/\//, '')
}

/**
 * Generates all possible domain variations for internal link detection
 */
const getDomainVariations = (): string[] => {
	const domain = getDomainFromBaseUrl()
	return [domain, `www.${domain}`]
}

/**
 * Checks if a link is internal (same domain as BASE_URL) or external
 */
const isInternalLink = (link: string): boolean => {
	// Handle relative paths and hash links
	if (link.startsWith('/') || link.startsWith('#')) {
		return true
	}

	// Remove protocol if present
	const cleanLink = link.replace(/^https?:\/\//, '')
	const domainVariations = getDomainVariations()

	// Check if it matches any domain variation (exact match or with path)
	return domainVariations.some(
		domain => cleanLink === domain || cleanLink.startsWith(`${domain}/`)
	)
}

/**
 * Extracts the path from various internal URL formats
 */
const extractInternalPath = (link: string): string => {
	// Handle hash links
	if (link.startsWith('#')) {
		return link
	}

	// Handle relative paths
	if (link.startsWith('/')) {
		return link
	}

	// Remove protocol
	let cleanLink = link.replace(/^https?:\/\//, '')

	// Remove any matching domain variation
	const domainVariations = getDomainVariations()
	for (const domain of domainVariations) {
		if (cleanLink.startsWith(domain)) {
			cleanLink = cleanLink.replace(domain, '')
			break
		}
	}

	// Ensure path starts with /
	if (cleanLink && !cleanLink.startsWith('/')) {
		cleanLink = '/' + cleanLink
	}

	// Return / if empty path
	return cleanLink || '/'
}

export const processLink = (
	link: string
): {
	href: string
	target?: string
	rel?: string
} => {
	if (!link) {
		return {
			href: '#'
		}
	}

	// mailto: / tel: — keep as-is (no new tab, no https:// prefix)
	if (/^(mailto|tel):/i.test(link)) {
		return {
			href: link
		}
	}

	// Handle API URL replacement (keep existing functionality)
	if (process.env.NEXT_PUBLIC_API_URL && link.startsWith(process.env.NEXT_PUBLIC_API_URL)) {
		return {
			href: link.replace(process.env.NEXT_PUBLIC_API_URL, '')
		}
	}

	// Check if the link is internal to medante.sk
	if (isInternalLink(link)) {
		return {
			href: extractInternalPath(link)
		}
	}

	// External link - open in new tab
	return {
		target: '_blank',
		rel: 'noopener noreferrer',
		href: link.startsWith('http') ? link : `https://${link}`
	}
}

/**
 * Processes all anchor tags in an HTML string: normalizes internal links to paths
 * and ensures external links have target="_blank" and rel="noopener noreferrer".
 * Uses processLink() for each href. Safe to call only in browser (uses DOMParser).
 */
export const processHtmlLinks = (html: string): string => {
	if (typeof window === 'undefined' || !html) return html
	const parser = new DOMParser()
	const doc = parser.parseFromString(html, 'text/html')
	doc.querySelectorAll('a[href]').forEach(anchor => {
		const href = anchor.getAttribute('href')
		if (href) {
			const processed = processLink(href)
			anchor.setAttribute('href', processed.href)
			if (processed.target) anchor.setAttribute('target', processed.target)
			else anchor.removeAttribute('target')
			if (processed.rel) anchor.setAttribute('rel', processed.rel)
			else anchor.removeAttribute('rel')
		}
	})
	return doc.body.innerHTML
}
